import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

interface StackEntry {
  id: string;
  type: 'dialog' | 'sheet' | 'sub-navigation' | 'section';
  onClose: () => void;
}

interface NavigationStackContextType {
  push: (entry: Omit<StackEntry, 'id'>) => string;
  remove: (id: string) => void;
  pop: () => void;
  hasEntries: () => boolean;
  clear: () => void;
}

const NavigationStackContext = createContext<NavigationStackContextType | undefined>(undefined);

export const NavigationStackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stack, setStack] = useState<StackEntry[]>([]);
  const stackRef = useRef<StackEntry[]>([]);
  
  // Track if we're currently handling a popstate event (to prevent history.back() loops)
  const isHandlingPopState = useRef(false);
  // Track pending removes that were triggered by UI close (not browser back)
  const pendingRemoves = useRef<Set<string>>(new Set());

  // Keep ref in sync with state
  useEffect(() => {
    stackRef.current = stack;
  }, [stack]);

  const push = useCallback((entry: Omit<StackEntry, 'id'>) => {
    const id = `${entry.type}-${Date.now()}-${Math.random()}`;
    const newEntry = { ...entry, id };
    
    // Update ref synchronously BEFORE history action
    stackRef.current = [...stackRef.current, newEntry];
    setStack(stackRef.current);
    
    // Use queueMicrotask to ensure React state update completes before pushState
    queueMicrotask(() => {
      // Preserve any existing history state (e.g. { section }) so we never lose section context
      const prevState = window.history.state ?? {};
      window.history.pushState({ ...prevState, navigationStackId: id }, '');
    });
    
    return id;
  }, []);

  const remove = useCallback((id: string) => {
    // Check if this entry exists in the stack
    const entryExists = stackRef.current.some(entry => entry.id === id);
    if (!entryExists) return;
    
    // Update ref synchronously BEFORE history action
    stackRef.current = stackRef.current.filter(entry => entry.id !== id);
    setStack(stackRef.current);
    
    // If we're NOT handling a popstate event, this means the dialog was closed via UI
    // We need to clean up the history entry WITHOUT triggering navigation
    if (!isHandlingPopState.current) {
      // Check if the current history state matches this entry
      const currentState = window.history.state;
      if (currentState?.navigationStackId === id) {
        // Use replaceState instead of history.back() to remove the dialog entry
        // without navigating away from the current section/tab
        queueMicrotask(() => {
          const { navigationStackId, ...rest } = window.history.state ?? {};
          window.history.replaceState(rest, '');
        });
      }
    }
  }, []);

  const pop = useCallback(() => {
    const currentStack = stackRef.current;
    if (currentStack.length === 0) return;
    
    const topEntry = currentStack[currentStack.length - 1];
    
    // Update ref synchronously BEFORE calling onClose
    stackRef.current = currentStack.slice(0, -1);
    setStack(stackRef.current);
    
    // Call the onClose callback
    topEntry.onClose();
  }, []);

  const hasEntries = useCallback(() => {
    return stackRef.current.length > 0;
  }, []);

  const clear = useCallback(() => {
    setStack([]);
  }, []);

  // Handle browser back button - register in CAPTURE mode to intercept before Index.tsx
  // This ensures overlays close before section navigation happens
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const navStackId = event.state?.navigationStackId;
      
      // If this is a pending remove (triggered by UI close calling history.back()),
      // just clean up and don't process further
      if (navStackId && pendingRemoves.current.has(navStackId)) {
        pendingRemoves.current.delete(navStackId);
        return;
      }
      
      // If we have entries in the stack, close the top overlay
      // This is based on stack state, NOT event.state.navigationStackId
      // because event.state describes the TARGET state, not where we came from
      if (stackRef.current.length > 0) {
        // Stop propagation to prevent Index.tsx from also handling this
        event.stopImmediatePropagation();
        
        isHandlingPopState.current = true;
        pop();
        isHandlingPopState.current = false;
      }
    };

    // Use capture mode to intercept BEFORE other handlers (like Index.tsx)
    window.addEventListener('popstate', handlePopState, { capture: true });
    return () => window.removeEventListener('popstate', handlePopState, { capture: true });
  }, [pop]);

  return (
    <NavigationStackContext.Provider value={{ push, remove, pop, hasEntries, clear }}>
      {children}
    </NavigationStackContext.Provider>
  );
};

export const useNavigationStack = () => {
  const context = useContext(NavigationStackContext);
  if (context === undefined) {
    throw new Error('useNavigationStack must be used within a NavigationStackProvider');
  }
  return context;
};
